import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Grid,
  Dialog,
  TextField,
  IconButton,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { Event, ComplianceItem } from '@/types/events';
import { v4 as uuidv4 } from 'uuid';

interface ComplianceTrackingProps {
  event: Event;
  onUpdate: (updatedEvent: Event) => void;
}

export const ComplianceTracking: React.FC<ComplianceTrackingProps> = ({
  event,
  onUpdate
}) => {
  const [selectedItem, setSelectedItem] = useState<ComplianceItem | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleAddItem = () => {
    setSelectedItem({
      id: uuidv4(),
      requirement: '',
      status: 'pending',
      dueDate: new Date().toISOString(),
      documents: []
    });
    setIsDialogOpen(true);
  };

  const handleSaveItem = (item: ComplianceItem) => {
    const updatedCompliance = selectedItem?.id
      ? event.compliance.map(i => i.id === item.id ? item : i)
      : [...event.compliance, item];
    onUpdate({ ...event, compliance: updatedCompliance });
    setIsDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDeleteItem = (itemId: string) => {
    const updatedCompliance = event.compliance.filter(i => i.id !== itemId);
    onUpdate({ ...event, compliance: updatedCompliance });
  };

  const getCompletionRate = () => {
    const total = event.compliance.length;
    if (total === 0) return 0;
    const completed = event.compliance.filter(item => item.status === 'completed').length;
    return (completed / total) * 100;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Compliance Tracking</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
          >
            Add Requirement
          </Button>
        </Stack>

        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Compliance Progress
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <LinearProgress
                variant="determinate"
                value={getCompletionRate()}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {Math.round(getCompletionRate())}%
            </Typography>
          </Box>
        </Paper>

        <Grid container spacing={2}>
          {event.compliance.map((item) => (
            <Grid item xs={12} key={item.id}>
              <Paper sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box>
                      <Typography variant="subtitle1">
                        {item.requirement}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        <Chip
                          label={item.status.replace('_', ' ')}
                          color={
                            item.status === 'completed' ? 'success' :
                            item.status === 'pending' ? 'warning' :
                            'default'
                          }
                          size="small"
                        />
                        <Chip
                          label={`Due: ${new Date(item.dueDate).toLocaleDateString()}`}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => setSelectedItem(item)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteItem(item.id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {item.documents.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Documents
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        {item.documents.map((doc) => (
                          <Chip
                            key={doc.id}
                            label={doc.filename}
                            size="small"
                            onDelete={() => {/* Handle document deletion */}}
                          />
                        ))}
                      </Stack>
                    </Box>
                  )}

                  <Button
                    startIcon={<UploadIcon />}
                    size="small"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Upload Document
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Stack>

      <Dialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {selectedItem?.id ? 'Edit Requirement' : 'Add Requirement'}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Requirement"
              value={selectedItem?.requirement || ''}
              onChange={(e) => setSelectedItem(prev => 
                prev ? { ...prev, requirement: e.target.value } : null
              )}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Due Date"
              type="date"
              value={selectedItem?.dueDate.split('T')[0] || ''}
              onChange={(e) => setSelectedItem(prev => 
                prev ? { ...prev, dueDate: new Date(e.target.value).toISOString() } : null
              )}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              select
              label="Status"
              value={selectedItem?.status || 'pending'}
              onChange={(e) => setSelectedItem(prev => 
                prev ? { ...prev, status: e.target.value as ComplianceItem['status'] } : null
              )}
              fullWidth
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="not_applicable">Not Applicable</option>
            </TextField>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => selectedItem && handleSaveItem(selectedItem)}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
};